<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Historial de vacaciones</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">

          <!-- Izquierda: botón filtrar + acciones mobile -->
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>
              <div class="d-md-none">
                <button type="button" class="button-tooltip text-dark font-weight-bold m-0"
                  v-b-tooltip.hover title="Recargar" @click="loadData()">
                  <i class="ri-loop-right-line"></i>
                </button>
                <button type="button" class="button-tooltip text-dark font-weight-bold mr-0"
                  v-b-tooltip.hover title="Descargar Excel" @click="downloadReport()">
                  <i class="ri-download-2-line"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Derecha: acciones desktop -->
          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover title="Recargar" @click="loadData()">
                <i class="ri-loop-right-line"></i>
              </button>
              <button type="button" class="button-tooltip text-dark font-weight-bold ml-0"
                v-b-tooltip.hover title="Descargar Excel" @click="downloadReport()">
                <i class="ri-download-2-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto md-flex-1 d-flex flex-column flex-md-row">
              <SearchBar
                placeholder="Buscar por nombre o número de documento"
                @search="onSearch"
                @input="search = $event"
                class="flex-1"
              />
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>

        <div
          class="w-100 d-flex flex-wrap align-items-center mt-2"
          v-if="showFiltersList"
        >
          <span
            v-for="(val, name) in filterList"
            v-bind:key="name"
            class="filter-item"
          >
            {{ getFilterName(name, val) }}
            <button
              type="button"
              class="text-white p-0 bg-transparent border-0 ml-2"
              @click="deleteFilter(name)"
            >
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <!-- Tabla desktop -->
      <div class="table-responsive d-none d-md-block">
        <table class="custom-table">
          <thead>
            <tr>
              <th>COLABORADOR</th>
              <th>EMPRESA</th>
              <th>ÁREA</th>
              <th>FECHA INICIO</th>
              <th>FECHA FIN</th>
              <th>DÍAS</th>
              <th>REVISADO POR</th>
              <th>ESTADO</th>
              <th class="text-center">OPCIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="list.length === 0">
              <td colspan="9" class="text-center text-muted py-4">No se encontraron registros</td>
            </tr>
            <tr v-for="item in paginatedList" :key="item.id">
              <td>{{ item.employeeName || '-' }}</td>
              <td>{{ item.companyName || '-' }}</td>
              <td>{{ item.areaName || '-' }}</td>
              <td>{{ item.startDate || '-' }}</td>
              <td>{{ item.endDate || '-' }}</td>
              <td>{{ item.calendarDays || '-' }}</td>
              <td class="text-muted small">{{ getReviewedBy(item) }}</td>
              <td>
                <span :class="['badge', getStatusColor(item.status)]">{{ item.status }}</span>
              </td>
              <td class="text-center">
                <b-dropdown variant="link" no-caret toggle-class="p-0">
                  <template #button-content>
                    <i class="ri-more-2-fill text-dark"></i>
                  </template>
                  <b-dropdown-item :to="{ name: 'vacation-requests-detail', params: { id: item.id } }">
                    Ver detalle
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tabla mobile -->
      <div class="d-md-none">
        <div v-if="list.length === 0" class="text-center text-muted py-4">No se encontraron registros</div>
        <div v-for="item in paginatedList" :key="item.id" class="mobile-card mb-3 p-3 border rounded">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <strong>{{ item.employeeName }}</strong>
              <div class="text-muted small">{{ item.companyName }} · {{ item.areaName }}</div>
            </div>
            <span :class="['badge', getStatusColor(item.status)]">{{ item.status }}</span>
          </div>
          <div class="mt-2 small">
            {{ item.startDate }} — {{ item.endDate }}
            <span class="ml-2">({{ item.calendarDays }} días)</span>
          </div>
          <div class="mt-2">
            <router-link
              :to="{ name: 'vacation-requests-detail', params: { id: item.id } }"
              class="button button-light button-sm"
            >Ver detalle</router-link>
          </div>
        </div>
      </div>

      <!-- Footer: paginación -->
      <div class="table-footer d-flex justify-content-between align-items-center px-3 py-2">
        <span class="text-muted small">{{ list.length }} registro(s) encontrado(s)</span>
        <div v-if="pages > 1" class="d-flex align-items-center">
          <button
            type="button"
            class="button-tooltip"
            :disabled="page === 1"
            @click="page--"
          ><i class="ri-arrow-left-s-line"></i></button>
          <span class="mx-2 small">Página {{ page }} de {{ pages }}</span>
          <button
            type="button"
            class="button-tooltip"
            :disabled="page === pages"
            @click="page++"
          ><i class="ri-arrow-right-s-line"></i></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Constants,
  Alert,
  FilterMenu,
  FilterMixin,
  SearchBar,
} from 'wize-admin'
import { VacationService, CompanyService, AreaService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

const PAGE_SIZE = 20

export default {
  name: 'VacationHistory',
  components: { FilterMenu, SearchBar },
  mixins: [FilterMixin],

  data() {
    return {
      list:        [],
      showFilters: false,
      page:        1,
      search:      '',

      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: Constants.TypeFilters.SINGLE,
          options: Object.values(LocalConstants.VacationRequestStatus).map(s => ({ key: s.key, label: s.label })),
        },
        {
          label: 'Empresa',
          key: 'companyId',
          type: Constants.TypeFilters.SINGLE,
          options: [],
        },
        {
          label: 'Área',
          key: 'areaId',
          type: Constants.TypeFilters.SINGLE,
          options: [],
        },
        {
          label: 'Fecha de inicio',
          key: 'startDate',
          type: Constants.TypeFilters.DATE,
          isRange: true,
        },
      ],
    }
  },

  computed: {
    pages() {
      return Math.max(1, Math.ceil(this.list.length / PAGE_SIZE))
    },
    paginatedList() {
      const start = (this.page - 1) * PAGE_SIZE
      return this.list.slice(start, start + PAGE_SIZE)
    },
  },

  methods: {
    buildFilterParams() {
      const params = {}
      if (this.filters.status)          params.status    = this.filters.status
      if (this.filters.companyId)       params.companyId = this.filters.companyId
      if (this.filters.areaId)          params.areaId    = this.filters.areaId
      if (this.filters.startDateStart)  params.startDate = this.filters.startDateStart
      if (this.filters.startDateEnd)    params.endDate   = this.filters.startDateEnd
      if (this.search)                  params.search    = this.search
      return params
    },

    onSearch(val) {
      this.search = val
      this.loadData()
    },

    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getRequests(this.buildFilterParams())
        this.list = resp.payload || []
        this.page = 1
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el historial')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async downloadReport() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.downloadXlsx(this.buildFilterParams())
        const today = new Date().toISOString().split('T')[0]
        const url  = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href  = url
        link.setAttribute('download', `historial-vacaciones-${today}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el reporte')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    getStatusColor(status) {
      const found = Object.values(LocalConstants.VacationRequestStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },

    getReviewedBy(item) {
      if (item.status === 'APROBADA')  return item.approvedBy  || '-'
      if (item.status === 'CANCELADA') return item.cancelledBy || '-'
      return '-'
    },
  },

  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.filterOptions[1].options = (companiesResp.payload || []).map(c => ({
        key: c.id, label: c.businessName || c.name,
      }))
      this.filterOptions[2].options = (areasResp.payload || []).map(a => ({
        key: a.id, label: a.description,
      }))
    } catch (e) {
      console.error('Error al cargar catálogos del historial', e)
    }
    this.loadData()
  },
}
</script>
